import {
  type CheckoutQuoteResponse,
  extractRelayFeeInfo,
  extractRelayReceiveAmount,
  fetchExactInQuote,
  toWithdrawalAmountBaseUnit,
} from '@funkit/connect-core'
import type { FeeBreakdownV2 } from '@funkit/api-base'
import { useQuery } from '@tanstack/react-query'
import type { Address } from 'viem'

import type { WithdrawalToken } from '../flow/withdrawalFlowConfig'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { useSourceTokenDecimals } from './useSourceTokenDecimals'

export const CRYPTO_WITHDRAWAL_QUOTE_QUERY_KEY = 'cryptoWithdrawalQuote'

/**
 * How long a quote stays good for. Used for BOTH `staleTime` and
 * `refetchInterval`, and the equality is load-bearing: with `staleTime: 0`, an
 * `enabled` flip mid-edit remounts the query and refetches the previous key
 * before the new amount lands, replaying a stale quote. The interval keeps it
 * fresh regardless.
 */
export const QUOTE_REFRESH_INTERVAL_MS = 25_000

export interface CryptoWithdrawalQuoteView {
  /** Executed as-is by the pipeline — never rebuilt from the fields below. */
  raw: CheckoutQuoteResponse
  /** What the recipient gets, in destination-token units. */
  receiveAmount: string | undefined
  fees: FeeBreakdownV2
}

export interface UseCryptoWithdrawalQuoteParams {
  sourceToken: WithdrawalToken
  destinationToken: WithdrawalToken | undefined
  recipientAddress: string
  /** Source-token units. Already debounced and settled by the caller. */
  amount: string
  walletAddress: string
  /** The caller's own gate — validation, risk, blacklist (see `useQuoteGate`). */
  enabled: boolean
}

/**
 * The EXACT_IN quote for a crypto withdrawal, refreshed on an interval so the
 * fees on screen stay the fees that execute.
 *
 * The query key is the inputs themselves — no context round-trip — so a quote
 * can never describe a different withdrawal than the form shows.
 */
export function useCryptoWithdrawalQuote({
  sourceToken,
  destinationToken,
  recipientAddress,
  amount,
  walletAddress,
  enabled,
}: UseCryptoWithdrawalQuoteParams) {
  const { apiKey } = useFunkitIdentity()
  const decimals = useSourceTokenDecimals(sourceToken)

  const query = useQuery({
    queryKey: [
      CRYPTO_WITHDRAWAL_QUOTE_QUERY_KEY,
      apiKey,
      sourceToken.chainId,
      sourceToken.address,
      destinationToken?.chainId,
      destinationToken?.address,
      recipientAddress,
      amount,
      // The quote is signed for one sender, so a wallet switch must not read a
      // cached quote built for the previous one.
      walletAddress,
      decimals,
    ],
    queryFn: async (): Promise<CryptoWithdrawalQuoteView> => {
      if (!destinationToken) {
        throw new Error('Quote requested without a destination token')
      }
      if (decimals == null) {
        throw new Error('Quote requested before the source decimals resolved')
      }
      const raw = await fetchExactInQuote({
        apiKey,
        logger,
        fromChainId: sourceToken.chainId,
        fromTokenAddress: sourceToken.address,
        toChainId: destinationToken.chainId,
        toTokenAddress: destinationToken.address,
        recipientAddress: recipientAddress as Address,
        userAddress: walletAddress as Address,
        fromAmountBaseUnit: toWithdrawalAmountBaseUnit(amount, decimals),
      })
      return {
        raw,
        receiveAmount: extractRelayReceiveAmount(raw.metadata, logger),
        fees: extractRelayFeeInfo(raw.metadata, logger),
      }
    },
    enabled:
      enabled &&
      !!apiKey &&
      !!destinationToken &&
      !!recipientAddress &&
      Number(amount) > 0 &&
      decimals != null,
    staleTime: QUOTE_REFRESH_INTERVAL_MS,
    refetchInterval: QUOTE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    // A failed quote is shown, not retried behind the user's back: the inputs
    // usually need changing, and a silent retry would delay that feedback.
    retry: false,
  })

  return {
    quote: query.data,
    // Only the first fetch — `isFetching` would blank the rows and disable the
    // CTA on every background refresh.
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
